/**
 * Modal window wrapper around the chatbot base.
 *
 * See chatbot_modal.scss
 */
import * as Sentry from '@sentry/browser';
import lottie from 'lottie-web';
import { parse } from 'marked';

import { buildURL, fetchRequest } from 'Utils/apiFetchRequest';
import { secondsToTimestamp } from 'Utils/text';

const Copy = {
    placeholder: {
        default: 'What are the latest perspectives on...',
        loading: 'Searching...',
    },
    trigger: 'Ask Vumedi AI',
    error: {
        questionResponse: 'Sorry, I could not compute.',
    },
};

export default class VuMediChatbotModal {
    constructor() {
        this.triggerElement = null;
        this.modalElement = null;
        this.uuid = null;
        this.inProgress = false;
        this.skeletonMessageInterval = null;
        this.activeEventSource = null;
        this.searchField = document.querySelector('.js_navSearch-input');
    }

    _handleStreamingAnswerResponse(answerElement, question, type = '') {
        return new Promise((resolve) => {
            this.activeEventSource = new EventSource(
                `/api/chats/${this.uuid}/ask/?question=${encodeURIComponent(question)}&question_source=${encodeURIComponent(type)}`,
            );

            let processedFirstResponse = false;

            this.activeEventSource.addEventListener('message', (ev) => {
                try {
                    if (!processedFirstResponse) {
                        this._toggleSkeleton(true, false);
                        processedFirstResponse = true;
                        this._toggleSkeleton(false);
                    }

                    const answerData = JSON.parse(ev.data);
                    this._answerRenderContent(answerElement, answerData.answer, answerData.next_best_questions);

                    if (answerData['last_response']) {
                        const nextBestQuestions =
                            answerData && Array.isArray(answerData['next_best_questions'])
                                ? answerData['next_best_questions']
                                : [];

                        this._answerRenderContent(answerElement, answerData.answer, nextBestQuestions);
                        this.activeEventSource.close();
                        resolve();
                    }
                } catch (error) {
                    this.activeEventSource.close();
                    this._answerRenderContent(answerElement, Copy.error.questionResponse);
                    Sentry.captureException(error);
                    resolve();
                }
            });

            this.activeEventSource.addEventListener('error', (error) => {
                this.activeEventSource.close();
                this._answerRenderContent(answerElement, Copy.error.questionResponse);
                Sentry.captureException(error);
                resolve();
            });
        });
    }

    async _askQuestion(question, type = '') {
        if (!this.modalElement) {
            return;
        }

        this.inProgress = true;

        this.modalElement.querySelector('.vm-chatbot-body').classList.add('vm-chatbot-body--conversation');
        this.modalElement.querySelector('input[type="text"]').value = '';
        this.modalElement.querySelector('input[type="text"]').setAttribute('placeholder', Copy.placeholder.loading);
        this.modalElement
            .querySelector('.vm-chatbot-footer button[type="submit"]')
            .setAttribute('disabled', 'disabled');
        this.modalElement.querySelector('.js_vmChatbotBrowseAllWrapper')?.classList.add('is-hidden');

        this._renderQuestion(question);
        this._toggleSkeleton(true);

        const answerElement = this._answerPrepareElement();

        await this._handleStreamingAnswerResponse(answerElement, question, type);

        this.inProgress = false;
        this._toggleSkeleton(false);

        if (this.modalElement?.hasAttribute('open')) {
            // Browse all results button
            this.modalElement.querySelector('.js_vmChatbotBrowseAllWrapper')?.classList.remove('is-hidden');
            const browseAllButton = this.modalElement.querySelector('.js_vmChatbotBrowseAllButton');
            browseAllButton.querySelector('span').innerText =
                `Browse all videos on ‘${this.searchField.dataset.userInput}’`;
            browseAllButton.href = `/search/?q=${encodeURIComponent(this.searchField.dataset.userInput)}`;

            // Search input filed states
            this.modalElement.querySelector('.vm-chatbot-footer button[type="submit"]').removeAttribute('disabled');
            this.modalElement.querySelector('input[type="text"]').setAttribute('placeholder', Copy.placeholder.default);

            // Automatic scrolling
            this._answerComplete();
        }
    }

    _handleCloseClick() {
        if (!this.modalElement) {
            return;
        }

        this.modalElement.close();
        this.modalElement.querySelector('.vm-chatbot-body').classList.remove('vm-chatbot-body--conversation');
        this.modalElement.querySelector('.js_vmChatbotBrowseAllWrapper')?.classList.add('is-hidden');

        if (this.activeEventSource) {
            try {
                this.activeEventSource.close();
                this.activeEventSource = null;
            } catch (error) {
                Sentry.captureException(error);
            }
        }

        document.body.style.overflow = '';

        const messages = this.modalElement.querySelectorAll('.vm-chatbot-messages .vm-chatbot-message');

        if (!messages?.length) {
            return;
        }

        messages.forEach((el) => el.remove());
    }

    _browseButtonMarkup() {
        return `
            <div class="vm-chatbot-messages__browse-all_wrapper is-hidden js_vmChatbotBrowseAllWrapper">
                <a href="#" class="vm-button vm-button--primary vm-button--sm js_vmChatbotBrowseAllButton">
                    <span>Browse all videos</span>
                    <i class="icon icon-chevron-right"></i>
                </a>
            </div>
        `;
    }

    _renderModalElement() {
        const markup = `<dialog class="vm-chatbot-modal-container">
            <div class="vm-chatbot-modal">
                <div class="vm-chatbot-header">
                    ${this._vumediAiLogo()}
                    <button type="button" class="vm-button vm-button--sm vm-button--secondary vm-button--chatbot-close is- js_vmChatbotClose">
                        <span>Close</span>
                        <i class="icon icon-x"></i>
                    </button>
                </div>
                <div class="vm-chatbot-body js_chatbotBody">
                    <div class="vm-chatbot-body__loader js_chatbotLoader"></div>
                    <div class="vm-chatbot-placeholder">
                        <div class="vm-chatbot-placeholder__icon">
                            <i class="icon icon-chatbot"></i>
                        </div>
                        <h4 class="vm-chatbot-placeholder__title">Ask <span>AI</span> anything!</h4>
                        <p class="vm-chatbot-placeholder__text">or anything else you are looking for.</p>
                        <form class="vm-chatbot-placeholder__form">
                            <i class="icon icon-chatbot"></i>
                            <input type="text"
                                name="question"
                                class="vm-chatbot-input"
                                autocomplete="off"
                                placeholder="${Copy.placeholder.default}"
                                aria-label="Question for AI" />
                            <button type="submit" class="vm-button vm-button--sm vm-button--primary vm-chatbot-send js_chatbotSend">
                                <i class="icon icon-arrow-up"></i>
                            </button>
                        </form>
                    </div>
                    <div class="vm-chatbot-messages">
                        <div class="vm-chatbot-skeleton js_vmChatbotSkeleton">
                            <div class="vm-chatbot-skeleton__teaser-message-wrap">
                                <div class="vm-chatbot-skeleton__teaser-message-avatar js_chatbotTeaserMessageAvatar"></div>
                                <div class="vm-chatbot-skeleton__teaser-message-text js_chatbotTeaserMessageText">
                                    <span>Understanding Vumedi videos...</span>
                                    <span style="display: none;">Searching most recent updates...</span>
                                    <span style="display: none;">Finding relevant videos...</span>
                                </div>
                            </div>
                            <div class="vm-chatbot-skeleton__lines-wrap">
                                ${this._skeletonLines()}
                            </div>
                        </div>
                        ${this._browseButtonMarkup()}
                    </div>
                </div>
                <div class="vm-chatbot-footer">
                    <form class="vm-chatbot-footer__form">
                        <i class="icon icon-chatbot"></i>
                        <input type="text"
                               name="question"
                               class="vm-chatbot-input"
                               autocomplete="off"
                               placeholder="${Copy.placeholder.default}"
                               data-type="user_question"
                               aria-label="Question for AI" />
                        <button type="submit" class="vm-button vm-button--sm vm-button--primary vm-chatbot-send js_chatbotSend">
                            <i class="icon icon-arrow-up"></i>
                        </button>
                    </form>
                </div>
            </div>
        </dialog>`;

        const template = document.createElement('template');
        template.innerHTML = markup;

        const containerElement = template.content.firstChild;

        containerElement.addEventListener('close', () => this._handleCloseClick());
        containerElement.querySelector('.js_vmChatbotClose').addEventListener('click', (ev) => {
            ev.preventDefault();
            this._handleCloseClick();
        });

        const forms = Array.from(containerElement.querySelectorAll('form'));

        forms.forEach((form) => {
            form.addEventListener('submit', (ev) => {
                ev.preventDefault();

                if (this.inProgress) {
                    return;
                }

                const inputElement = form.querySelector('input[type="text"]');

                const question = inputElement.value.trim();
                inputElement.value = '';

                if (!question) {
                    return;
                }

                this._askQuestion(question, inputElement.getAttribute('data-type') || '');
            });
        });

        return containerElement;
    }

    _renderQuestion(question) {
        if (!this.modalElement) {
            return;
        }

        const questionElement = document.createElement('div');
        const skeletonElement = this.modalElement.querySelector('.js_vmChatbotSkeleton');
        questionElement.classList.add('vm-chatbot-message', 'vm-chatbot-message--question');
        questionElement.innerText = question;

        const iconElement = document.createElement('i');
        iconElement.classList.add('icon', 'icon-chatbot');
        questionElement.prepend(iconElement);

        const messagesElement = this.modalElement.querySelector('.vm-chatbot-messages');
        messagesElement.insertBefore(questionElement, skeletonElement);

        setTimeout(() => {
            messagesElement.scrollTo({
                top: messagesElement.scrollHeight,
                behavior: 'smooth',
            });
        }, 0);
    }

    _answerPrepareElement() {
        const answerElement = document.createElement('div');
        answerElement.classList.add('vm-chatbot-message', 'vm-chatbot-message--answer');

        return answerElement;
    }

    _answerPrettifyLinks(links) {
        const prettifyLink = (linkElement) => {
            const hrefAttribute = linkElement.getAttribute('href');

            if (!hrefAttribute) return;

            const linkHref = new URL(hrefAttribute);
            const timestamp = parseInt(linkHref.searchParams.get('t') || '', 10);
            const samePath = window.location.pathname === linkHref.pathname;

            const textElement = document.createElement('span');
            textElement.innerText = linkElement.innerText;
            linkElement.innerText = '';
            linkElement.appendChild(textElement);

            const iconElement = document.createElement('i');
            iconElement.classList.add('icon', 'icon-play-circle');
            linkElement.prepend(iconElement);

            if (Number.isInteger(timestamp)) {
                const timestampElement = document.createElement('strong');
                const timestampValue = secondsToTimestamp(timestamp);

                timestampElement.innerText = `[${timestampValue}]`;
                linkElement.appendChild(timestampElement);
            }

            if (samePath) {
                if (Number.isInteger(timestamp)) {
                    textElement.innerText = '';
                }

                linkElement.addEventListener('click', (ev) => {
                    ev.preventDefault();

                    if (Number.isInteger(timestamp)) {
                        this.brightcovePlayer?._player.currentTime(timestamp);
                    }
                });
            } else {
                linkElement.setAttribute('target', '_blank');
            }
        };

        links.forEach((el) => prettifyLink(el));
    }

    _answerRenderContent(answerElement, answer, nextBestQuestions = []) {
        if (!this.modalElement) {
            return;
        }

        const skeletonElement = this.modalElement.querySelector('.js_vmChatbotSkeleton');

        answerElement.innerHTML = parse(answer);

        this._answerPrettifyLinks(answerElement.querySelectorAll('a'));

        const messagesElement = this.modalElement.querySelector('.vm-chatbot-messages');
        messagesElement.insertBefore(answerElement, skeletonElement);

        if (nextBestQuestions?.length) {
            const nextBestQuestionsElement = this._renderNextBestQuestions(nextBestQuestions);
            messagesElement.insertBefore(nextBestQuestionsElement, skeletonElement);
        }
    }

    // Scroll so the last question is at the top of the viewport
    _answerComplete() {
        const messagesElement = this.modalElement.querySelector('.vm-chatbot-messages');
        const lastQuestionElement = (() => {
            const questions = messagesElement.querySelectorAll('.vm-chatbot-message--question');

            if (!questions?.length) {
                return null;
            }

            return questions[questions.length - 1];
        })();

        if (!lastQuestionElement) {
            return;
        }

        messagesElement.scrollTo({
            top: Math.max(lastQuestionElement.offsetTop - lastQuestionElement.offsetHeight, 0),
            behavior: 'smooth',
        });
    }

    _renderNextBestQuestions(nextBestQuestions) {
        const nbqContainer = document.createElement('div');
        nbqContainer.classList.add('vm-chatbot-message', 'vm-chatbot-message--answer', 'vm-chatbot-message--nbq');

        const nbqHeading = document.createElement('h3');
        nbqHeading.innerText = 'Follow-up questions...';

        nbqContainer.appendChild(nbqHeading);

        nextBestQuestions.forEach((question) => {
            const questionElement = document.createElement('button');
            questionElement.setAttribute('type', 'button');
            questionElement.classList.add('vm-chatbot--nbq');

            const iconElement = document.createElement('i');
            iconElement.classList.add('icon', 'icon-chatbot');

            const textElement = document.createElement('span');
            textElement.innerText = question.question;

            questionElement.appendChild(iconElement);
            questionElement.appendChild(textElement);

            questionElement.addEventListener('click', (ev) => {
                ev.preventDefault();
                this._askQuestion(question.question, question.type);
            });

            nbqContainer.appendChild(questionElement);
        });

        return nbqContainer;
    }

    _renderModal() {
        this.modalElement = this._renderModalElement();
        document.body.appendChild(this.modalElement);
        this._loadAnimation();
    }

    /**
     * Retrieve predefined questions for the provided content ID.
     *
     * @returns {Promise<{
     *   status: 'error' | 'forbidden' | 'success',
     *   predefined: Array<{ question: QuestionObject }>,
     * }>}
     */
    _retrievePredefined() {
        return new Promise((resolve) => {
            resolve({
                status: 'success',
                predefined: [
                    {
                        id: 11,
                        question: 'What are the latest perspectives on %s?',
                        type: 'predefined',
                    },
                    {
                        id: 12,
                        question: 'What are the pros and cons of %s?',
                        type: 'predefined',
                    },
                    {
                        id: 13,
                        question: 'What recent studies are there on %s?',
                        type: 'predefined',
                    },
                    {
                        id: 14,
                        question: '%s',
                        type: 'user_question',
                    },
                ],
            });
        });
    }

    _initializeChatbot() {
        return new Promise((resolve, reject) => {
            const requestURL = buildURL('/api/chats/chat-configuration/', { content_type: 683 });

            fetchRequest(requestURL)
                .then(() => {
                    resolve();
                })
                .catch((error) => {
                    reject();
                    Sentry.captureException(error);
                });
        });
    }

    _loadAnimation() {
        const lottieAnimationLandingPath = document.querySelector('meta[name="lottie-ai-landing"]')?.content;
        const lottieAnimationAnswerPath = document.querySelector('meta[name="lottie-ai-answer"]')?.content;

        if (lottieAnimationLandingPath) {
            const windowLoadAnimation = lottie.loadAnimation({
                container: this.modalElement.querySelector('.js_chatbotLoader'),
                renderer: 'svg',
                loop: true,
                autoplay: true,
                path: lottieAnimationLandingPath,
            });

            windowLoadAnimation.addEventListener('DOMLoaded', () => {
                windowLoadAnimation.setSpeed(0.66);
                windowLoadAnimation.playSegments([45, 135], true);
            });
        }

        if (lottieAnimationAnswerPath) {
            const answerLoadAnimation = lottie.loadAnimation({
                container: this.modalElement.querySelector('.js_chatbotTeaserMessageAvatar'),
                renderer: 'svg',
                loop: true,
                autoplay: true,
                path: lottieAnimationAnswerPath,
            });

            answerLoadAnimation.addEventListener('DOMLoaded', () => {
                answerLoadAnimation.setSpeed(0.66);
                answerLoadAnimation.playSegments([45, 164], true);
            });
        }
    }

    _vumediAiLogo() {
        const textualLogo = '<h3>Vumedi <strong>AI<i class="icon icon-chatbot-minimal"></i></strong></h3>';
        const logoSource = document.querySelector('meta[name="vumedi-ai-logo"]')?.content;
        const imgLogo = `<img class="vm-chatbot-header__logo" src="${logoSource}">`;

        return logoSource ? imgLogo : textualLogo;
    }

    _skeletonLines() {
        const lines = [];
        for (let i = 0; i < 16; i++) {
            const index = (i + 1) % 4 > 0 ? (i + 1) % 4 : 4;
            lines.push(`<div class="vm-chatbot-skeleton__line vm-chatbot-skeleton__line--${index}"></div>`);
        }
        return lines.join('');
    }

    _toggleSkeleton(show = true, showText = true) {
        if (show) {
            this.modalElement.querySelector('.js_vmChatbotSkeleton').classList.add('is-visible');

            if (showText) {
                this.modalElement
                    .querySelector('.vm-chatbot-skeleton__teaser-message-wrap')
                    ?.classList.remove('hidden');

                if (!this.skeletonMessageInterval) {
                    const spans = this.modalElement.querySelectorAll('.js_chatbotTeaserMessageText span');
                    let current = 0;

                    // Hide all spans except the first one
                    spans.forEach((span, i) => {
                        span.style.display = i === 0 ? 'inline' : 'none';
                    });

                    this.skeletonMessageInterval = setInterval(() => {
                        // Hide current
                        spans[current].style.display = 'none';

                        // Move to next (loop around)
                        current = (current + 1) % spans.length;

                        // Show next
                        spans[current].style.display = 'block';
                    }, 3000);
                }
            } else {
                this.modalElement.querySelector('.vm-chatbot-skeleton__teaser-message-wrap')?.classList.add('hidden');
            }
        } else {
            this.modalElement.querySelector('.js_vmChatbotSkeleton').classList.remove('is-visible');

            if (this.skeletonMessageInterval) {
                clearInterval(this.skeletonMessageInterval);
                this.skeletonMessageInterval = null;
            }
        }
    }

    async initialize(triggerContainer) {
        const { status, predefined } = await this._retrievePredefined();

        if (status === 'forbidden' || status === 'error') {
            return;
        }

        try {
            await this._initializeChatbot();
            triggerContainer.classList.remove('hidden');
            this._renderModal();

            return {
                initialized: true,
                predefined,
            };
        } catch {
            return {
                initialized: false,
                predefined: [],
            };
        }
    }

    startSearch(question = '') {
        if (!this.modalElement) {
            return;
        }

        // this is a failsafe to remove any previous messages which might have been left in the modal
        // this can happen if the user closes the modal before the answer was received
        Array.from(this.modalElement.querySelectorAll('.vm-chatbot-message')).forEach((el) => {
            el.remove();
        });

        this.modalElement.querySelector('.vm-chatbot-body').classList.remove('vm-chatbot-body--conversation');

        this.modalElement.querySelector('.vm-chatbot-body').classList.add('is-loading');

        if (!this.modalElement.hasAttribute('open')) {
            this.modalElement.setAttribute('data-state', 'ready');
            this.modalElement.showModal();
            document.body.style.overflow = 'hidden';
        }

        const options = {
            method: 'POST',
            body: JSON.stringify({
                content_type: 683,
            }),
        };

        fetchRequest('/api/chats/', options)
            .then((data) => {
                if (!data?.uuid) {
                    Sentry.captureException(new Error('Invalid response from the server'));
                    return;
                }

                this.uuid = data.uuid;
                this.inProgress = false;

                this.modalElement.querySelector('.vm-chatbot-body').classList.remove('is-loading');
                this.modalElement.querySelector('button[type="submit"]').removeAttribute('disabled');
                this.modalElement.querySelector('input[type="text"]').focus();
                this.modalElement
                    .querySelector('input[type="text"]')
                    .setAttribute('placeholder', Copy.placeholder.default);

                if (question && this.modalElement) {
                    this.modalElement.querySelector('input[type="text"]').value = question;
                    this.modalElement.querySelector('input[type="text"]').setAttribute('data-type', 'predefined');
                    this.modalElement.querySelector('button[type="submit"]').click();
                } else {
                    this.modalElement.querySelector('input[type="text"]').setAttribute('data-type', 'user_question');
                }
            })
            .catch((error) => {
                this.modalElement?.close();
                this.modalElement?.removeAttribute('data-state');
                document.body.style.overflow = '';
                Sentry.captureException(error);
            });
    }
}
